import type { PrivacyChoice } from '../routes/Privacy';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { ApiError } from '@dru/api-client';
import { api } from './api';
import { destroy as destroyOfflineStore } from '../offline/db';
import { purgeCaches } from '../pwa/register';

/**
 * The worker's session.
 *
 * Two differences from the staff console, both consequences of who is holding
 * the phone:
 *
 *   · a worker opens this app three to five times a year, so being logged out
 *     between visits is the norm rather than a failure. The refresh token has
 *     a long life and the app resumes silently; asking a factory worker to
 *     re-enter an OTP because a token expired overnight is how an app stops
 *     being used.
 *
 *   · there is no password anywhere. The account is provisioned on first OTP
 *     and can only ever be reached that way.
 */

export interface Employee {
  id: string;
  fullName: string;
  employeeCode: string;
}

interface AuthState {
  employee: Employee | null;
  bootstrapping: boolean;
  /** The employee code only when the mobile is shared; the data-use choice only when the server asked for it. */
  requestOtp(employeeCode: string | null, mobile: string): Promise<{ challengeToken: string; sentTo: string }>;
  verifyOtp(challengeToken: string, code: string, privacyConsent?: PrivacyChoice | null): Promise<void>;
  logout(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

const STORED_NAME = 'dru.employee';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Through the client, not a hand-rolled fetch. The client
        // de-duplicates concurrent rotations; a raw fetch here skipped that,
        // so a bootstrap rotation racing a screen's first 401 presented the
        // same refresh token twice — and reuse detection reads that as theft
        // and kills the family. The worker gets signed out by opening the app.
        const resumed = await api.resumeSession();
        if (resumed) {
          // The profile is the identity check: if it fails, the session is not
          // usable regardless of what the refresh returned.
          const p = await api.get<{ id: string; fullName: string; employeeCode: string }>('/me/profile');
          setEmployee({ id: p.id, fullName: p.fullName, employeeCode: p.employeeCode });
          // Only the display name is cached, so a returning worker sees their
          // own name while the network call is in flight. No clinical content
          // and no identifiers beyond what is already on their ID card —
          // localStorage on a shared handset is not a safe place for either.
          localStorage.setItem(STORED_NAME, p.fullName);
        }
      } catch {
        // No session; the router sends us to /login.
      } finally {
        setBootstrapping(false);
      }
    })();
  }, []);

  useEffect(() => api.onTokenChange((t) => { if (!t) setEmployee(null); }), []);

  const requestOtp = useCallback(async (employeeCode: string | null, mobile: string) => {
    return api.post<{ challengeToken: string; sentTo: string; expiresInSeconds: number }>(
      '/auth/otp/request', { mobile, ...(employeeCode ? { employeeCode } : {}) },
    );
  }, []);

  const verifyOtp = useCallback(async (challengeToken: string, code: string, privacyConsent?: PrivacyChoice | null) => {
    const res = await api.post<{
      accessToken: string;
      employee: { id: string; fullName: string; employeeCode: string };
    }>('/auth/otp/verify', { challengeToken, code, ...(privacyConsent ? { privacyConsent } : {}) });
    api.setToken(res.accessToken);
    setEmployee(res.employee);
    localStorage.setItem(STORED_NAME, res.employee.fullName);
  }, []);

  /**
   * Sign-out clears the device FIRST, and never waits on the network to do it.
   *
   * docs/14 is explicit about the order and the reason: a worker handing their
   * phone to someone else must not be told "log out failed, try again online".
   * So the IndexedDB store is destroyed and the shell caches are purged
   * locally, and only then is the server told — best effort, because the
   * refresh token expiring is a backstop for a revocation that never lands.
   */
  const logout = useCallback(async () => {
    api.setToken(null);
    setEmployee(null);
    localStorage.removeItem(STORED_NAME);

    // Local, unconditional, before anything that can hang.
    await destroyOfflineStore();
    await purgeCaches();

    await api.post('/auth/logout').catch(() => undefined);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ employee, bootstrapping, requestOtp, verifyOtp, logout }),
    [employee, bootstrapping, requestOtp, verifyOtp, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/**
 * Errors a worker can act on.
 *
 * The API's problem details are written for a person, so they are shown as-is.
 * The fallback avoids technical wording: "Something went wrong" is honest and
 * actionable; "Request failed with status 500" is neither, to this reader.
 */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.problem.detail || e.problem.title;
  if (e instanceof Error && e.message) return e.message;
  return 'Something went wrong. Please try again.';
}
